import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { requestSuggestion } from "../../../abstract/api/rest";
import { StatusTypes } from "../../../abstract/controller/enum/dnd/StatusTypes";
import * as nodeUtils from "../../../abstract/controller/utils/nodeUtils";
import { isNonEmptyString } from "../../../abstract/controller/utils/systemUtils";
import useRequestContainer from "../../../abstract/hook/useRequestContainer";
import useStatus from "../../../abstract/hook/useStatus";
import { getEndpointAI } from "../../../abstract/redux/slice/endpointSlice";
import { getErrors, getIsExtended, setMetaExtended } from "../../../abstract/redux/slice/metaSlice";
import {
  getIsContextMenuDisplayed,
  getWindowLock,
  setContextMenu,
} from "../../../abstract/redux/slice/overlaySlice";
import { isStatusDragged } from "../../../abstract/redux/slice/statusSlice";
import {
  handleFunctionCopy,
  handleFunctionRemove,
  handleFunctionRename,
} from "../../../abstract/redux/slice/structureSlice";
import IconMenuVertical from "../../../themes/icons/IconMenuVertical";
import TooltipNode from "../../tooltip/TooltipNode";
import IconError from "../../ui/icons/IconError";
import Menu from "../../ui/menu/Menu";
import DefaultTooltip, * as vendorTooltip from "../../ui/tooltips/DefaultTooltip";
import styles from "./FunctionHeader.module.scss";

const _inputExists = "↳ Name is not unique";
const _inputEmpty = "↳ Name is not defined";
const _inputColonStart = "↳ Name cannot start with ':'";
const _inputColonEnd = "↳ Name cannot end with ':'";
const _inputColonDouble = "↳ Name cannot contain '::'";

const _placeholderSuggestion = "Loading suggestion...";
const _placeholderDefault = "Function name";

const FunctionHeaderContext = createContext();

function FunctionHeader({ node }) {
  const _dispatch = useDispatch();

  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);
  const _status = useStatus(_id);
  const _windowLock = useSelector(getWindowLock);
  const _isContextMenuDisplayed = useSelector(getIsContextMenuDisplayed(_id));
  const [_isMouseOver, _setMouseOver] = useState(false);
  const [_labelError, _setLabelError] = useState("");
  const [_placeholder, _setPlaceholder] = useState(_placeholderDefault);
  const [_showModal, _setShowModal] = useState(false);
  const _ref = useRef();

  const _name = nodeUtils.getName(node) ?? "";

  const _errors = useSelector(getErrors(_uuid));
  const _isExtended = useSelector(getIsExtended(_uuid));

  const _isHidden = isStatusDragged(_status) || _windowLock;

  const _position = _ref?.current?.getBoundingClientRect();
  const _contextLeft = _position ? _position.right + 5 : 0;
  const _contextTop = _position ? _position.top : 0;

  //---------------------------------------------------------------------

  const clearContextMenu = () => {
    if (_isContextMenuDisplayed) {
      _dispatch(setContextMenu(""));
    }
  };

  const toggleContextMenu = () => {
    _dispatch(setContextMenu(_isContextMenuDisplayed ? "" : _id));
  };

  const handleOnMouseOver = (e) => {
    e.stopPropagation();

    if (_windowLock) {
      return;
    }

    _setMouseOver(true);
  };

  const handleOnMouseOut = (e) => {
    e.stopPropagation();

    if (_windowLock) {
      return;
    }

    _setMouseOver(false);
  };

  return (
    <FunctionHeaderContext.Provider
      value={{
        isContextMenuDisplayed: _isContextMenuDisplayed,
        clearContextMenu,
        toggleContextMenu,
        node,
        id: _id,
        name: _name,
        status: _status,
        placeholder: _placeholder,
        setPlaceholder: _setPlaceholder,
        labelError: _labelError,
        setLabelError: _setLabelError,
        showModal: _showModal,
        setShowModal: _setShowModal,
        isExtended: _isExtended,
        isMouseOver: _isMouseOver,
        uuid: _uuid,
        isHidden: _isHidden,
        errors: _errors,
      }}>
      <div
        ref={_ref}
        className={styles["header"]}
        onMouseOver={handleOnMouseOver}
        onMouseOut={handleOnMouseOut}>
        <Header />
      </div>
      <div className={styles["context-position"]} style={{ left: _contextLeft, top: _contextTop }}>
        <WindowOptions />
      </div>
    </FunctionHeaderContext.Provider>
  );
}

function Header() {
  const { name, uuid, isMouseOver } = useContext(FunctionHeaderContext);

  const _nonEmpty = isNonEmptyString(name);

  return (
    <div id={`head-${uuid}`}>
      <form
        id={`head-form-${uuid}`}
        className={styles["header__form"]}
        onSubmit={(e) => e.preventDefault()}>
        <HeaderInputName />
        <HeaderError />
        {_nonEmpty && isMouseOver && <HeaderIconOptions />}
      </form>
    </div>
  );
}

function HeaderError() {
  const { labelError, uuid } = useContext(FunctionHeaderContext);

  if (!labelError) {
    return null;
  }

  return (
    <label id={`error-${uuid}`} className={styles["header__error"]}>
      {labelError}
    </label>
  );
}

function HeaderInputName() {
  const _dispatch = useDispatch();
  const { errors, status, labelError, setLabelError, node, isHidden, placeholder, setPlaceholder } =
    useContext(FunctionHeaderContext);

  const _endpointAI = useSelector(getEndpointAI);
    
  const _id = nodeUtils.getID(node);
  const _uuid = nodeUtils.getUUID(node);
  const _name = nodeUtils.getName(node);

  const _container = useRequestContainer({ id: _id, type: "function" });

  const [_inputName, _setInputName] = useState(_name);

  const _nameRef = useRef(null);
  const _suggestionTimeoutRef = useRef(null);
  const _abortRef = useRef(null);

  const [_isLoadingSuggestion, _setIsLoadingSuggestion] = useState(false);

  const _children = nodeUtils.getFunctions(_container.nodeParent);
  const _childrenNames = _children?.map((e) => nodeUtils.getName(e)) ?? [];

  const getClassStyle = () => {
    if (status === StatusTypes.DRAGGED) {
      return `${styles["style--dragging"]}`;
    }

    return labelError ? styles["style--stroke"] : "";
  };

  const getClassName = () => {
    return `${styles["header__name-input"]} ${getClassStyle()}`;
  };

  const clearSuggestionTimeout = useCallback(() => {
    if (_suggestionTimeoutRef.current) {
      clearTimeout(_suggestionTimeoutRef.current);
    }

    _setIsLoadingSuggestion(false);
  }, []);

  const actionChangeNameAccept = useCallback(() => {
    clearSuggestionTimeout();

    if (!labelError && _inputName) {
      _dispatch(handleFunctionRename(_id, _inputName));
    }
  }, [clearSuggestionTimeout, labelError, _inputName, _dispatch, _id]);

  const actionChangeNameCancel = useCallback(() => {
    clearSuggestionTimeout();

    if (_name) {
      _setInputName(_name);
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      _dispatch(handleFunctionRemove(_id));
    }
  }, [clearSuggestionTimeout, _name, setLabelError, setPlaceholder, _dispatch, _id]);

  const handleInputNameOnChange = (e) => {
    const _value = e.target.value;

    _setInputName(_value);

    if (_value.startsWith(":")) {
      setLabelError(_inputColonStart);
    } else if (_value.endsWith(":")) {
      setLabelError(_inputColonEnd);
    } else if (_value.includes("::")) {
      setLabelError(_inputColonDouble);
    } else if (_childrenNames.includes(_value) && _name !== _value) {
      setLabelError(_inputExists);
    } else if (!_value) {
      setLabelError(_inputEmpty);
    } else {
      setLabelError("");
    }

    setPlaceholder(_placeholderDefault);

    if (labelError === "") {
      if (_value && _value.trim() !== "") {
        if (_nameRef.current.selectionEnd >= _nameRef.current.textLength) {
          suggestNameSuffix(_value);
        } else {
          clearSuggestionTimeout();
        }
      }
    }
  };

  const handleInputNameFocusIn = () => {
    if (!_name && (!_inputName || _inputName.trim() === "")) {
      suggestName();
    } else {
      _nameRef.current.select();
    }
  };

  const handleInputNameFocusOut = () => {
    if (_isLoadingSuggestion) {
      return;
    }

    clearSuggestionTimeout();

    if (labelError) {
      if (_name) {
        _setInputName(_name);
        _dispatch(handleFunctionRename(_id, _name));
      } else {
        _dispatch(handleFunctionRemove(_id));
      }
      setLabelError("");
      setPlaceholder(_placeholderDefault);
    } else {
      if (_inputName) {
        _dispatch(handleFunctionRename(_id, _inputName));
      } else {
        if (_name) {
          _dispatch(handleFunctionRename(_id, _name));
        } else {
          _dispatch(handleFunctionRemove(_id));
        }
      }
    }
  };

  const suggestName = useCallback(async () => {
    if (!_endpointAI) {
      return;
    }

    _setIsLoadingSuggestion(true);
    setPlaceholder(_placeholderSuggestion);
    
    _abortRef.current = new AbortController();

    try {
      const _suggestion = await requestSuggestion({
        controller: _abortRef.current,
        ..._container.request,
      });

      if (_nameRef.current) {
        if (_suggestion.status !== "error" && !_nameRef.current.value) {
          _setInputName(_suggestion.message);

          _nameRef.current.value = _suggestion.message;
          _nameRef.current.select();
        } else {
          setPlaceholder("Suggestion error");
        }
      }
    } catch (error) {}

    clearSuggestionTimeout();
  }, [_container.request, _endpointAI, clearSuggestionTimeout, setPlaceholder]);

  const suggestNameSuffix = useCallback(
    async (prefix) => {
      if (!_endpointAI) {
        return;
      }
      if (_suggestionTimeoutRef.current) {
        return;
      }

      _abortRef.current = new AbortController();

      _suggestionTimeoutRef.current = setTimeout(async () => {
        _setIsLoadingSuggestion(true);

        try {
          const _suggestion = await requestSuggestion({
            controller: _abortRef.current,
            prefix,
            ..._container.request,
          });

          if (_suggestion.status !== "error") {
            _setInputName(_suggestion.message);

            if (_nameRef.current) {
              _nameRef.current.value = _suggestion.message;
              _nameRef.current.setSelectionRange(prefix.length, _suggestion.message.length);
            }
          }
        } catch (error) {}

        clearSuggestionTimeout();
      }, 2000);
    },
    [_container.request, _endpointAI, clearSuggestionTimeout]
  );

  useEffect(() => {
    if (status === StatusTypes.DRAGGED) {
      _dispatch(setMetaExtended(_uuid, false));
      _nameRef.current.blur();
    }
  }, [status, _dispatch, _uuid]);

  useEffect(() => {
    if (!_name) {
      _nameRef.current.focus();
    } else {
      _nameRef.current.blur();
    }
  }, [_name]);

  useEffect(() => {
    const _target = _nameRef.current;

    const callback = (e) => {
      if (e.code === "Enter") {
        if (!_isLoadingSuggestion) {
          actionChangeNameAccept();
        }
      } else if (e.code === "Escape") {
        actionChangeNameCancel();
      } else if (e.code === "ArrowRight") {
        if (_target.selectionEnd + 1 >= _target.textLength) {
          suggestNameSuffix(_target.defaultValue);
        }
      } else {
        clearSuggestionTimeout();
      }
    };

    _target?.addEventListener("keydown", callback);

    return () => _target?.removeEventListener("keydown", callback);
  }, [
    _isLoadingSuggestion,
    actionChangeNameAccept,
    actionChangeNameCancel,
    clearSuggestionTimeout,
    suggestNameSuffix,
  ]);

  return (
    <>
      <div className={styles["icon-error"]}>
        <IconError uuid={_uuid} errors={errors} />
      </div>
      <input
        id={`input-name-${_uuid}`}
        data-tooltip-id={`node-${_uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}
        ref={_nameRef}
        type="text"
        className={`${getClassName()} ${_isLoadingSuggestion ? styles.shimmer : ""}`}
        value={_inputName}
        placeholder={placeholder}
        onChange={handleInputNameOnChange}
        onFocus={handleInputNameFocusIn}
        onBlur={handleInputNameFocusOut}
        data-title={_name}
      />
      <TooltipNode node={node} handle={`node-${_uuid}`} isHidden={isHidden} />
    </>
  );
}

function HeaderIconOptions() {
  const { toggleContextMenu, uuid } = useContext(FunctionHeaderContext);

  const handleMenuToggle = () => {
    toggleContextMenu();
  };

  const getClass = () => {
    return `${styles["icons-options-menu"]}`;
  };

  return (
    <>
      <div
        className={getClass()}
        onClick={handleMenuToggle}
        data-tooltip-id={`tooltip-show-options-info-${uuid}`}
        data-tooltip-delay-show={vendorTooltip.getTimeFast()}>
        <IconMenuVertical id={`action-${uuid}`} />
      </div>
      <DefaultTooltip handle={`tooltip-show-options-info-${uuid}`}>
        Show function options
      </DefaultTooltip>
    </>
  );
}

function WindowOptions() {
  const _dispatch = useDispatch();
  const { isContextMenuDisplayed, clearContextMenu, id } = useContext(FunctionHeaderContext);

  const handleMenuRemoveFunction = () => {
    _dispatch(handleFunctionRemove(id));
  };

  const handleMenuCopyFunction = () => {
    _dispatch(handleFunctionCopy(id));
    clearContextMenu();
  };

  const getMenuItems = () => {
    const menuItems = [];

    menuItems.push({ name: "remove", action: handleMenuRemoveFunction });
    menuItems.push({ name: "copy", action: handleMenuCopyFunction });

    return menuItems;
  };

  return <Menu show={isContextMenuDisplayed} items={getMenuItems()} />;
}

export default FunctionHeader;
